import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import { FaUser, FaEnvelope, FaLock, FaEdit, FaSave, FaTimes, FaEye, FaEyeSlash, FaCheckCircle, FaExclamationTriangle, FaCalendarAlt, FaGoogle, FaShieldAlt, FaCheck, FaTimes as FaTimesIcon, FaUniversity, FaCreditCard, FaDollarSign } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { apiBaseUrl } from '../utils/apiBaseUrl';
import { useSocket } from '../contexts/SocketContext';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';

// DEBUG: Profile.js - Component imports loaded successfully
console.log('🔍 DEBUG: Profile.js - All imports loaded');
console.log('🔍 DEBUG: React hooks available:', { useState, useEffect, useCallback });
console.log('🔍 DEBUG: Router hooks available:', { useNavigate, useLocation, useSearchParams });
console.log('🔍 DEBUG: Auth context available:', { useAuth });
console.log('🔍 DEBUG: External libraries available:', { axios, styled });
console.log('🔍 DEBUG: Icons imported successfully');

// DEBUG: Check for any variable conflicts in global scope
console.log('🔍 DEBUG: Profile.js - Debug logging initialized');

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;
const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1rem;
  animation: ${fadeIn} 220ms ease-out both;
  
  @media (max-width: 768px) {
    padding: 0.9rem 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 0.75rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 1.1rem;
  
  h1 {
    color: #2c3e50;
    font-size: 1.8rem;
    margin-bottom: 0.4rem;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: 1.6rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.4rem;
    }
  }
  
  p {
    color: #7f8c8d;
    font-size: 0.9rem;
    font-family: 'Inter', sans-serif;
    max-width: 520px;
    margin: 0.5rem auto 0 auto;
    
    @media (max-width: 480px) {
      font-size: 0.875rem;
    }
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  margin-bottom: 1.1rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: ${slideUp} 180ms ease-out both;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #15803d 0%, #22c55e 100%);
  color: white;
  padding: 0.7rem 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
  }
  
  button {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const CardContent = styled.div`
  padding: 0.9rem;
`;

const PersonalInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.7rem;
  }
`;

const InfoCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.85rem;
  transition: all 0.2s ease;
  animation: ${slideUp} 180ms ease-out both;
  
  &:hover {
    border-color: #22c55e;
    box-shadow: 0 2px 4px rgba(34, 197, 94, 0.1);
  }
  
  .label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
    font-family: 'Inter', sans-serif;
  }
  
  .value {
    font-size: 0.95rem;
    color: #1f2937;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    svg {
      color: #22c55e;
      font-size: 0.9rem;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
  }
  
  .input-wrapper {
    position: relative;
    
    svg {
      position: absolute;
      left: 0.875rem;
      top: 50%;
      transform: translateY(-50%);
      color: #6b7280;
      font-size: 0.9rem;
    }
    
    .toggle-password {
      position: absolute;
      right: 0.875rem;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      
      &:hover {
        color: #22c55e;
      }
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem 0.6rem 2.25rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
  }
  
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
    border-color: #e5e7eb;
  }
  
  &.password {
    padding-right: 2.75rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  
  button {
    padding: 0.6rem 1.2rem;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    
    &.cancel {
      background: #95a5a6;
      color: white;
      
      &:hover {
        background: #7f8c8d;
      }
    }
    
    &.save {
      background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
      color: white;
      
      &:hover {
        transform: translateY(-2px);
      }
      
      &:disabled {
        background: #95a5a6;
        cursor: not-allowed;
        transform: none;
      }
    }
    
    svg {
      font-size: 0.9rem;
      flex-shrink: 0;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.65rem;
  margin-bottom: 1.1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.45rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.75rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  border-left: 3px solid #22c55e;
  animation: ${slideUp} 180ms ease-out both;
  
  &:nth-child(1) { animation-delay: 0ms; }
  &:nth-child(2) { animation-delay: 40ms; }
  &:nth-child(3) { animation-delay: 80ms; }
  &:nth-child(4) { animation-delay: 120ms; }
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
  
  .stat-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #22c55e;
    margin-bottom: 0.25rem;
    font-family: 'Inter', sans-serif;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
  
  .stat-label {
    color: #6b7280;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    
    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
  }
`;

const AlertMessage = styled.div`
  padding: 0.9rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  &.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  
  &::after {
    content: '';
    width: 30px;
    height: 30px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #22c55e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PasswordStrengthIndicator = styled.div`
  margin-top: 0.5rem;
  
  .strength-bar {
    height: 4px;
    background: #e5e7eb;
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 0.5rem;
    
    .strength-fill {
      height: 100%;
      transition: all 0.3s ease;
      border-radius: 2px;
      
      &.weak {
        width: 25%;
        background: #ef4444;
      }
      
      &.fair {
        width: 50%;
        background: #f59e0b;
      }
      
      &.good {
        width: 75%;
        background: #10b981;
      }
      
      &.strong {
        width: 100%;
        background: #059669;
      }
    }
  }
  
  .strength-text {
    font-size: 0.75rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    
    &.weak { color: #ef4444; }
    &.fair { color: #f59e0b; }
    &.good { color: #10b981; }
    &.strong { color: #059669; }
  }
`;

const PasswordRequirements = styled.div`
  margin-top: 0.5rem;
  
  .requirement {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
    
    &.met {
      color: #10b981;
    }
    
    &.unmet {
      color: #6b7280;
    }
    
    svg {
      font-size: 0.7rem;
    }
  }
`;

const UserTypeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 8px;
  margin-bottom: 1.1rem;
  border: 1px solid #cbd5e1;
  
  .user-type-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    
    &.password-only {
      background: linear-gradient(135deg, #22c55e 0%, #15803d 100%);
      color: white;
    }
    
    &.hybrid {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
    }
    
    &.google-only {
      background: linear-gradient(135deg, #4285f4 0%, #1a73e8 100%);
      color: white;
    }
  }
  
  .user-type-info {
    flex: 1;
    
    .user-type-title {
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 0.25rem;
      font-size: 0.9rem;
    }
    
    .user-type-description {
      color: #6b7280;
      font-size: 0.8rem;
      line-height: 1.4;
    }
  }
`;

const ValidationMessage = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &.error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }
  
  &.success {
    background: #f0fdf4;
    color: #16a34a;
    border: 1px solid #bbf7d0;
  }
`;

const Profile = () => {
  // DEBUG: Profile component initialization
  console.log('🔍 DEBUG: Profile component - Starting initialization');

  const { user, refreshUser, linkGoogleAccount, getUserProfile } = useAuth();
  const { socket } = useSocket();
  console.log('🔍 DEBUG: Profile - useAuth destructured:', { user: !!user, refreshUser: !!refreshUser, linkGoogleAccount: !!linkGoogleAccount, getUserProfile: !!getUserProfile });
  console.log('🔍 DEBUG: Profile - useSocket destructured:', { socket: !!socket });

  const navigate = useNavigate();
  console.log('🔍 DEBUG: Profile - useNavigate hook initialized:', !!navigate);

  const location = useLocation();
  console.log('🔍 DEBUG: Profile - useLocation hook initialized:', { pathname: location?.pathname });

  const [searchParams, setSearchParams] = useSearchParams();
  console.log('🔍 DEBUG: Profile - useSearchParams hook initialized:', { searchParams: searchParams?.toString() });

  // Remove editingProfile state
  const [editingPassword, setEditingPassword] = useState(false);
  console.log('🔍 DEBUG: Profile - editingPassword state initialized:', false);

  const [loading, setLoading] = useState(false);
  console.log('🔍 DEBUG: Profile - loading state initialized:', false);

  const [message, setMessage] = useState({ type: '', text: '' });
  console.log('🔍 DEBUG: Profile - message state initialized:', { type: '', text: '' });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  console.log('🔍 DEBUG: Profile - showPasswords state initialized:', { current: false, new: false, confirm: false });

  // Remove profileData state as it's no longer needed for editing

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  console.log('🔍 DEBUG: Profile - passwordData state initialized:', { currentPassword: '', newPassword: '', confirmPassword: '' });

  const [passwordValidation, setPasswordValidation] = useState({
    strength: 'weak',
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false
    },
    confirmMatch: false,
    isValid: false
  });
  console.log('🔍 DEBUG: Profile - passwordValidation state initialized:', { strength: 'weak', isValid: false });

  const [hasPassword, setHasPassword] = useState(false);
  console.log('🔍 DEBUG: Profile - hasPassword state initialized:', false);

  const [stats, setStats] = useState({
    totalSurveys: 0,
    totalPoints: 0,
    totalRewards: 0,
    memberSince: ''
  });
  console.log('🔍 DEBUG: Profile - stats state initialized:', { totalSurveys: 0, totalPoints: 0, totalRewards: 0, memberSince: '' });

  // Bank account details state
  const [editingBankDetails, setEditingBankDetails] = useState(false);
  console.log('🔍 DEBUG: Profile - editingBankDetails state initialized:', false);

  const [bankData, setBankData] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: ''
  });
  console.log('🔍 DEBUG: Profile - bankData state initialized:', { accountHolderName: '', accountNumber: '', ifscCode: '' });

  const [originalBankData, setOriginalBankData] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: ''
  });
  console.log('🔍 DEBUG: Profile - originalBankData state initialized:', { accountHolderName: '', accountNumber: '', ifscCode: '' });

  // Bank details specific loading and retry states
  const [bankLoading, setBankLoading] = useState(false);
  const [bankRetryCount, setBankRetryCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  console.log('🔍 DEBUG: Profile - Bank loading states initialized:', { bankLoading: false, bankRetryCount: 0, isOnline: navigator.onLine });

  const [bankValidation, setBankValidation] = useState({
    accountHolderName: { isValid: true, message: '' },
    accountNumber: { isValid: true, message: '' },
    ifscCode: { isValid: true, message: '' },
    isFormValid: true
  });
  console.log('🔍 DEBUG: Profile - bankValidation state initialized:', { isFormValid: true });

  const fetchUserStats = useCallback(async () => {
    console.log('🔍 DEBUG: fetchUserStats - Function called');

    // Declare all variables at the top to avoid hoisting issues
    let apiResponse;
    let dashboardData;
    let userCreatedAt;
    let memberSince;
    let newStats;

    try {
      console.log('🔍 DEBUG: fetchUserStats - Making API call to /users/dashboard');
      apiResponse = await axios.get('/users/dashboard', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('🔍 DEBUG: fetchUserStats - API response received:', apiResponse.status);
      dashboardData = apiResponse.data.dashboard;
      console.log('🔍 DEBUG: fetchUserStats - Dashboard data:', dashboardData);
      userCreatedAt = new Date(dashboardData.user.createdAt);
      memberSince = userCreatedAt.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      });

      newStats = {
        totalSurveys: dashboardData.stats.totalSurveys || 0,
        totalPoints: dashboardData.stats.totalPointsEarned || 0,
        totalWithdrawalAmount: dashboardData.stats.approvedWithdrawalAmount || 0,
        memberSince: memberSince
      };
      console.log('🔍 DEBUG: fetchUserStats - Setting stats:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('🔍 DEBUG: fetchUserStats - Error:', error);
    }
  }, []);
  console.log('🔍 DEBUG: fetchUserStats useCallback initialized');

  const checkPasswordStatus = useCallback(async () => {
    console.log('🔍 DEBUG: checkPasswordStatus - Function called');

    // Declare all variables at the top to avoid hoisting issues
    let profileData;
    let fallbackValue;

    try {
      console.log('🔍 DEBUG: checkPasswordStatus - Getting user profile');
      profileData = await getUserProfile();
      console.log('🔍 DEBUG: checkPasswordStatus - Profile data received:', profileData);
      if (profileData) {
        console.log('🔍 DEBUG: checkPasswordStatus - Setting hasPassword:', profileData.hasPassword);
        setHasPassword(profileData.hasPassword);
      }
    } catch (error) {
      console.error('🔍 DEBUG: checkPasswordStatus - Error:', error);
      // Fallback to user object check
      fallbackValue = !!user?.password;
      console.log('🔍 DEBUG: checkPasswordStatus - Using fallback value:', fallbackValue);
      setHasPassword(fallbackValue);
    }
  }, [user, getUserProfile]);
  console.log('🔍 DEBUG: checkPasswordStatus useCallback initialized');

  // Bank account details functions - moved before useEffect
  const fetchBankDetails = useCallback(async () => {
    console.log('🔍 DEBUG: fetchBankDetails - Function called');

    // Declare all variables at the top to avoid hoisting issues
    let authToken;
    let apiResponse;
    let bankDetailsData;
    let formattedBankData;

    try {
      authToken = localStorage.getItem('token');
      console.log('🔍 DEBUG: fetchBankDetails - Token available:', !!authToken);
      console.log('🔍 DEBUG: fetchBankDetails - Making API call to /users/bank-details');

      apiResponse = await axios.get('/users/bank-details', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      console.log('🔍 DEBUG: fetchBankDetails - API response:', apiResponse.status, apiResponse.data);

      if (apiResponse.data.success) {
        bankDetailsData = apiResponse.data.bankDetails;
        console.log('🔍 DEBUG: fetchBankDetails - Bank details received:', bankDetailsData);

        formattedBankData = {
          accountHolderName: bankDetailsData.accountHolderName || '',
          accountNumber: bankDetailsData.accountNumber || '',
          ifscCode: bankDetailsData.ifscCode || ''
        };
        console.log('🔍 DEBUG: fetchBankDetails - Setting bank data:', formattedBankData);
        setBankData(formattedBankData);
        setOriginalBankData(formattedBankData);
      }
    } catch (error) {
      console.error('🔍 DEBUG: fetchBankDetails - Error:', error);
    }
  }, []);
  console.log('🔍 DEBUG: fetchBankDetails useCallback initialized');

  useEffect(() => {
    console.log('🔍 DEBUG: useEffect[user] - Hook triggered');
    console.log('🔍 DEBUG: useEffect[user] - User available:', !!user);
    console.log('🔍 DEBUG: useEffect[user] - Dependencies:', { user: !!user, fetchUserStats: !!fetchUserStats, checkPasswordStatus: !!checkPasswordStatus, fetchBankDetails: !!fetchBankDetails });

    if (user) {
      console.log('🔍 DEBUG: useEffect[user] - User exists, calling functions');
      // Remove profileData initialization
      fetchUserStats();
      checkPasswordStatus();
      fetchBankDetails();
    } else {
      console.log('🔍 DEBUG: useEffect[user] - No user, skipping function calls');
    }
  }, [user, fetchUserStats, checkPasswordStatus, fetchBankDetails]);

  // Handle URL parameters for survey completion refresh
  useEffect(() => {
    console.log('🔍 DEBUG: useEffect[searchParams] - Hook triggered');
    const completed = searchParams.get('completed');
    const refresh = searchParams.get('refresh');
    console.log('🔍 DEBUG: useEffect[searchParams] - URL params:', { completed, refresh });

    if (completed === 'true' && refresh === 'true') {
      console.log('🔍 DEBUG: useEffect[searchParams] - Survey completion detected, refreshing data');
      // Refresh user data and stats when returning from survey completion
      const refreshData = async () => {
        try {
          console.log('🔍 DEBUG: useEffect[searchParams] - Calling refreshUser');
          await refreshUser(); // Update user context with latest points
          console.log('🔍 DEBUG: useEffect[searchParams] - Calling fetchUserStats');
          await fetchUserStats(); // Refresh profile stats

          // Clean up URL parameters after refresh
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('completed');
          newSearchParams.delete('refresh');
          console.log('🔍 DEBUG: useEffect[searchParams] - Cleaning up URL params');
          setSearchParams(newSearchParams, { replace: true });
        } catch (error) {
          console.error('🔍 DEBUG: useEffect[searchParams] - Error refreshing profile data:', error);
        }
      };

      refreshData();
    } else {
      console.log('🔍 DEBUG: useEffect[searchParams] - No survey completion, skipping refresh');
    }
  }, [searchParams, setSearchParams, refreshUser, fetchUserStats]);

  // Network connectivity monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 NETWORK: Connection restored');
      setIsOnline(true);
      if (!isOnline) {
        setMessage({ type: 'success', text: 'Connection restored!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    };

    const handleOffline = () => {
      console.log('🌐 NETWORK: Connection lost');
      setIsOnline(false);
      setMessage({ type: 'error', text: 'No internet connection. Please check your network.' });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  // Socket.io listener for real-time point updates
  useEffect(() => {
    if (socket && user) {
      const handlePointsUpdate = (data) => {
        console.log('🔍 DEBUG: Profile - Received pointsUpdated event:', data);

        // Show toast notification based on the reason
        if (data.reason === 'withdrawal_approved') {
          toast.success(`Withdrawal approved! ${Math.abs(data.change)} points deducted.`);
        } else if (data.reason === 'withdrawal_rejected') {
          toast.info(`Withdrawal rejected. ${data.change} points restored to your account.`);
        }

        // Refresh user data and stats
        refreshUser();
        fetchUserStats();
      };

      socket.on('pointsUpdated', handlePointsUpdate);

      return () => {
        socket.off('pointsUpdated', handlePointsUpdate);
      };
    }
  }, [socket, user, refreshUser, fetchUserStats]);

  // Remove handleProfileEdit, handleProfileCancel, and handleProfileSave functions

  const handlePasswordEdit = () => {
    console.log('🔍 DEBUG: handlePasswordEdit - Function called');
    console.log('🔍 DEBUG: handlePasswordEdit - Setting editingPassword to true');
    setEditingPassword(true);
    setMessage({ type: '', text: '' });
    console.log('🔍 DEBUG: handlePasswordEdit - Function completed');
  };

  const handlePasswordCancel = () => {
    console.log('🔍 DEBUG: handlePasswordCancel - Function called');
    console.log('🔍 DEBUG: handlePasswordCancel - Resetting password editing state');
    setEditingPassword(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    console.log('🔍 DEBUG: handlePasswordCancel - Function completed');
  };



  const handleLinkGoogle = async () => {
    console.log('🔍 DEBUG: handleLinkGoogle - Function called');
    try {
      console.log('🔍 DEBUG: handleLinkGoogle - Redirecting to Google OAuth');
      // Redirect to Google OAuth for linking
      window.location.href = `${apiBaseUrl}/auth/google`;
    } catch (error) {
      console.error('🔍 DEBUG: handleLinkGoogle - Error:', error);
      setMessage({ type: 'error', text: 'Failed to link Google account' });
    }
  };

  const handlePasswordSave = async () => {
    console.log('🔍 DEBUG: handlePasswordSave - Function called');
    console.log('🔍 DEBUG: handlePasswordSave - Password data:', {
      hasNewPassword: !!passwordData.newPassword,
      hasConfirmPassword: !!passwordData.confirmPassword,
      hasCurrentPassword: !!passwordData.currentPassword,
      hasPassword,
      isValid: passwordValidation.isValid
    });

    // Enhanced validation
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      console.log('🔍 DEBUG: handlePasswordSave - Validation failed: Missing password fields');
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (hasPassword && !passwordData.currentPassword) {
      console.log('🔍 DEBUG: handlePasswordSave - Validation failed: Current password required');
      setMessage({ type: 'error', text: 'Current password is required' });
      return;
    }

    if (!passwordValidation.isValid) {
      console.log('🔍 DEBUG: handlePasswordSave - Validation failed: Password requirements not met');
      setMessage({ type: 'error', text: 'Please ensure your password meets all requirements and passwords match' });
      return;
    }

    console.log('🔍 DEBUG: handlePasswordSave - Validation passed, starting API call');
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const payload = {
        newPassword: passwordData.newPassword
      };

      // Only include current password if user has one
      if (hasPassword) {
        payload.currentPassword = passwordData.currentPassword;
      }

      const response = await axios.put(
        '/users/password',
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Password updated successfully! Your account is now more secure.' });
        setEditingPassword(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordValidation({
          strength: 'weak',
          requirements: {
            minLength: false,
            hasUppercase: false,
            hasLowercase: false,
            hasNumber: false,
            hasSpecialChar: false
          },
          confirmMatch: false,
          isValid: false
        });
        setShowPasswords({ current: false, new: false, confirm: false });

        // Auto-dismiss success message after 5 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      }
    } catch (error) {
      console.error('Password update error:', error);
      let errorMessage = 'Failed to update password. Please try again.';

      if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Invalid password format or current password is incorrect.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Current password is incorrect. Please try again.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many password change attempts. Please try again later.';
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    console.log('🔍 DEBUG: togglePasswordVisibility - Function called with field:', field);
    console.log('🔍 DEBUG: togglePasswordVisibility - Current showPasswords state:', showPasswords);
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    console.log('🔍 DEBUG: togglePasswordVisibility - Function completed');
  };

  // Password validation functions
  const validatePasswordStrength = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const metRequirements = Object.values(requirements).filter(Boolean).length;
    let strength = 'weak';

    if (metRequirements >= 5) strength = 'strong';
    else if (metRequirements >= 4) strength = 'good';
    else if (metRequirements >= 2) strength = 'fair';

    return { requirements, strength, isValid: metRequirements >= 4 };
  };

  const handleInputChange = (field, value, type = 'password') => {
    console.log('🔍 DEBUG: handleInputChange - Function called');
    console.log('🔍 DEBUG: handleInputChange - Parameters:', { field, valueLength: value?.length, type });
    console.log('🔍 DEBUG: handleInputChange - Current passwordData:', passwordData);

    setPasswordData(prev => ({ ...prev, [field]: value }));

    // Real-time password validation
    if (field === 'newPassword') {
      console.log('🔍 DEBUG: handleInputChange - Validating new password');
      const validation = validatePasswordStrength(value);
      const confirmMatch = value === passwordData.confirmPassword;
      console.log('🔍 DEBUG: handleInputChange - Password validation result:', { validation, confirmMatch });

      setPasswordValidation({
        ...validation,
        confirmMatch,
        isValid: validation.isValid && confirmMatch
      });
    }

    if (field === 'confirmPassword') {
      console.log('🔍 DEBUG: handleInputChange - Validating confirm password');
      const confirmMatch = value === passwordData.newPassword;
      console.log('🔍 DEBUG: handleInputChange - Confirm password match:', confirmMatch);
      setPasswordValidation(prev => ({
        ...prev,
        confirmMatch,
        isValid: prev.requirements && Object.values(prev.requirements).filter(Boolean).length >= 4 && confirmMatch
      }));
    }

    console.log('🔍 DEBUG: handleInputChange - Function completed');
  };

  const getUserTypeInfo = () => {
    const hasGoogleId = user.googleId;

    if (hasGoogleId && hasPassword) {
      return {
        type: 'hybrid',
        title: 'Hybrid Account',
        description: 'You can sign in with both Google and your password.',
        icon: FaShieldAlt
      };
    } else if (hasGoogleId) {
      return {
        type: 'google-only',
        title: 'Google Account User',
        description: 'You authenticate exclusively through Google. Your account security is managed by Google.',
        icon: FaGoogle
      };
    } else {
      return {
        type: 'password-only',
        title: 'Password Account',
        description: 'You sign in using your email and password.',
        icon: FaShieldAlt
      };
    }
  };

  const renderPasswordRequirements = () => {
    const requirements = [
      { key: 'minLength', text: 'At least 8 characters' },
      { key: 'hasUppercase', text: 'One uppercase letter' },
      { key: 'hasLowercase', text: 'One lowercase letter' },
      { key: 'hasNumber', text: 'One number' },
      { key: 'hasSpecialChar', text: 'One special character' }
    ];

    return (
      <PasswordRequirements>
        {requirements.map(req => (
          <div
            key={req.key}
            className={`requirement ${passwordValidation.requirements[req.key] ? 'met' : 'unmet'}`}
          >
            {passwordValidation.requirements[req.key] ? <FaCheck /> : <FaTimesIcon />}
            {req.text}
          </div>
        ))}
      </PasswordRequirements>
    );
  };

  // Other bank account details functions

  const handleBankEdit = () => {
    console.log('🔍 DEBUG: handleBankEdit - Function called');
    console.log('🔍 DEBUG: handleBankEdit - Setting editingBankDetails to true');
    setEditingBankDetails(true);
    setMessage({ type: '', text: '' });
    console.log('🔍 DEBUG: handleBankEdit - Function completed');
  };

  const handleBankCancel = () => {
    console.log('🔍 DEBUG: handleBankCancel - Function called');
    console.log('🔍 DEBUG: handleBankCancel - Current bankData:', bankData);
    console.log('🔍 DEBUG: handleBankCancel - Original bankData:', originalBankData);
    setEditingBankDetails(false);
    setBankData(originalBankData);
    // Reset validation state
    setBankValidation({
      accountHolderName: { isValid: true, message: '' },
      accountNumber: { isValid: true, message: '' },
      ifscCode: { isValid: true, message: '' },
      isFormValid: true
    });
    console.log('🔍 DEBUG: handleBankCancel - Function completed');
  };

  const validateBankField = (field, value) => {
    let isValid = true;
    let message = '';

    switch (field) {
      case 'accountHolderName':
        if (value && value.length < 2) {
          isValid = false;
          message = 'Account holder name must be at least 2 characters';
        } else if (value && !/^[a-zA-Z\s]+$/.test(value)) {
          isValid = false;
          message = 'Account holder name can only contain letters and spaces';
        }
        break;
      case 'accountNumber':
        if (value && (value.length < 9 || value.length > 18)) {
          isValid = false;
          message = 'Account number must be between 9-18 digits';
        } else if (value && !/^\d+$/.test(value)) {
          isValid = false;
          message = 'Account number can only contain digits';
        }
        break;
      case 'ifscCode':
        if (value && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
          isValid = false;
          message = 'IFSC code format: 4 letters + 0 + 6 alphanumeric (e.g., SBIN0123456)';
        }
        break;
      default:
        break;
    }

    return { isValid, message };
  };

  const handleBankInputChange = (field, value) => {
    console.log('🔍 DEBUG: handleBankInputChange - Function called');
    console.log('🔍 DEBUG: handleBankInputChange - Parameters:', { field, value });
    console.log('🔍 DEBUG: handleBankInputChange - Current bankData:', bankData);

    setBankData(prev => ({ ...prev, [field]: value }));

    // Validate the field
    console.log('🔍 DEBUG: handleBankInputChange - Validating field:', field);
    const validation = validateBankField(field, value);
    console.log('🔍 DEBUG: handleBankInputChange - Validation result:', validation);

    setBankValidation(prev => {
      const newValidation = {
        ...prev,
        [field]: validation
      };

      // Check if form is valid
      const isFormValid = Object.keys(newValidation)
        .filter(key => key !== 'isFormValid')
        .every(key => newValidation[key].isValid);

      console.log('🔍 DEBUG: handleBankInputChange - Form validity:', isFormValid);

      return {
        ...newValidation,
        isFormValid
      };
    });

    console.log('🔍 DEBUG: handleBankInputChange - Function completed');
  };

  const handleBankSave = async () => {
    console.log('🏦 BANK SAVE: Starting bank details save process');
    console.log('🏦 BANK SAVE: Current bank data:', bankData);
    console.log('🏦 BANK SAVE: Retry count:', bankRetryCount);
    console.log('🏦 BANK SAVE: Network status:', isOnline);

    // Check network connectivity first
    if (!isOnline) {
      console.log('🏦 BANK SAVE: No network connection detected');
      setMessage({ type: 'error', text: 'No internet connection. Please check your network and try again.' });
      return;
    }

    // Validate all fields before saving
    console.log('🏦 BANK SAVE: Validating form fields...');
    const validationResults = {
      accountHolderName: validateBankField('accountHolderName', bankData.accountHolderName),
      accountNumber: validateBankField('accountNumber', bankData.accountNumber),
      ifscCode: validateBankField('ifscCode', bankData.ifscCode)
    };
    console.log('🏦 BANK SAVE: Validation results:', validationResults);

    const isFormValid = Object.values(validationResults).every(result => result.isValid);
    console.log('🏦 BANK SAVE: Form is valid:', isFormValid);

    setBankValidation({
      ...validationResults,
      isFormValid
    });

    if (!isFormValid) {
      console.log('🏦 BANK SAVE: Validation failed, stopping save process');
      setMessage({ type: 'error', text: 'Please fix the validation errors before saving.' });
      return;
    }

    console.log('🏦 BANK SAVE: Setting bank loading state and clearing messages');
    setBankLoading(true);
    setMessage({ type: '', text: '' });

    const attemptSave = async (attempt = 1) => {
      try {
        const token = localStorage.getItem('token');
        console.log(`🏦 BANK SAVE: Attempt ${attempt} - Token retrieved:`, token ? 'Present' : 'Missing');

        if (!token) {
          throw new Error('No authentication token found. Please log in again.');
        }

        console.log(`🏦 BANK SAVE: Attempt ${attempt} - Making API request to /users/bank-details`);
        console.log('🏦 BANK SAVE: Request payload:', bankData);

        const startTime = Date.now();
        const response = await axios.put(
          '/users/bank-details',
          bankData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 15000 // 15 second timeout
          }
        );
        const endTime = Date.now();

        console.log(`🏦 BANK SAVE: Attempt ${attempt} - API request completed in`, endTime - startTime, 'ms');
        return response;
      } catch (error) {
        console.error(`🏦 BANK SAVE: Attempt ${attempt} failed:`, error.message);

        // Check if we should retry
        const shouldRetry = attempt < 3 && (
          error.code === 'ECONNABORTED' || // Timeout
          error.code === 'NETWORK_ERROR' || // Network error
          !error.response || // No response (network issue)
          (error.response?.status >= 500) // Server error
        );

        if (shouldRetry) {
          console.log(`🏦 BANK SAVE: Retrying in ${attempt * 1000}ms... (attempt ${attempt + 1}/3)`);
          setBankRetryCount(attempt);
          setMessage({ type: 'info', text: `Connection issue. Retrying... (${attempt}/3)` });

          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          return attemptSave(attempt + 1);
        }

        throw error;
      }
    };

    try {
      const response = await attemptSave();
      const endTime = Date.now();

      console.log('🏦 BANK SAVE: Response status:', response.status);
      console.log('🏦 BANK SAVE: Response data:', response.data);

      if (response.data && response.data.success) {
        console.log('🏦 BANK SAVE: Save successful, updating UI');
        setMessage({ type: 'success', text: 'Bank account details updated successfully!' });
        setEditingBankDetails(false);
        setOriginalBankData(bankData);
        setBankRetryCount(0); // Reset retry count on success

        // Refresh user context to immediately update bank details across the app
        try {
          console.log('🏦 BANK SAVE: Refreshing user context for immediate UI updates');
          await refreshUser();
          console.log('🏦 BANK SAVE: User context refreshed successfully');
        } catch (refreshError) {
          console.error('🏦 BANK SAVE: Error refreshing user context:', refreshError);
          // Don't fail the save operation if refresh fails
        }

        // Reset validation state
        setBankValidation({
          accountHolderName: { isValid: true, message: '' },
          accountNumber: { isValid: true, message: '' },
          ifscCode: { isValid: true, message: '' },
          isFormValid: true
        });

        // Auto-dismiss success message after 5 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 5000);
      } else {
        console.log('🏦 BANK SAVE: Unexpected response format:', response.data);
        throw new Error('Unexpected server response format');
      }
    } catch (error) {
      console.error('🏦 BANK SAVE: Error occurred:', error);
      console.error('🏦 BANK SAVE: Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        code: error.code,
        isNetworkError: !error.response,
        isTimeout: error.code === 'ECONNABORTED'
      });

      let errorMessage = 'Failed to update bank details. Please try again.';
      let errorDetails = '';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please check your internet connection and try again.';
        errorDetails = 'The server took too long to respond.';
      } else if (!error.response) {
        errorMessage = 'Network error. Please check your internet connection.';
        errorDetails = 'Unable to connect to the server.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Invalid bank details format.';
        errorDetails = 'Please check your input and try again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
        errorDetails = 'Your session may have expired.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions.';
        errorDetails = 'You may not have permission to update bank details.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
        errorDetails = 'The server encountered an internal error.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server is temporarily unavailable. Please try again later.';
        errorDetails = `Server returned status ${error.response.status}.`;
      }

      console.log('🏦 BANK SAVE: Setting error message:', errorMessage);
      console.log('🏦 BANK SAVE: Error details:', errorDetails);

      setMessage({
        type: 'error',
        text: errorDetails ? `${errorMessage} ${errorDetails}` : errorMessage
      });
    } finally {
      console.log('🏦 BANK SAVE: Clearing bank loading state');
      setBankLoading(false);
      setBankRetryCount(0); // Reset retry count when operation completes
    }
  };

  console.log('🔍 DEBUG: Profile Component - Render phase started');
  console.log('🔍 DEBUG: Profile Component - Current state:', {
    user: !!user,
    loading,
    editingPassword,
    editingBankDetails,
    hasPassword,
    message: message.text,
    stats
  });

  if (!user) {
    console.log('🔍 DEBUG: Profile Component - No user, showing loading spinner');
    return <LoadingSpinner />;
  }

  // Debug: Log user object to check properties
  console.log('🔍 DEBUG: Profile - User object:', user);
  console.log('🔍 DEBUG: Profile - user.googleId:', user?.googleId, 'Type:', typeof user?.googleId);
  console.log('🔍 DEBUG: Profile - hasPassword state:', hasPassword);

  // Log the getUserTypeInfo result
  console.log('🔍 DEBUG: Profile - Calling getUserTypeInfo');
  const userTypeInfo = getUserTypeInfo();
  console.log('🔍 DEBUG: Profile - getUserTypeInfo result:', userTypeInfo);

  console.log('🔍 DEBUG: Profile Component - About to render JSX');

  return (
    <ProfileContainer>
      <PageHeader>
        <h1>My Profile</h1>
        <p>Manage your account settings and view your activity</p>
      </PageHeader>

      {message.text && (
        <AlertMessage className={message.type}>
          {message.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {message.text}
        </AlertMessage>
      )}

      <StatsGrid>
        <StatCard>
          <div className="stat-value">{stats.totalSurveys}</div>
          <div className="stat-label">Surveys Completed</div>
        </StatCard>
        <StatCard>
          <div className="stat-value">{stats.totalPoints.toLocaleString()}</div>
          <div className="stat-label">Total Points Earned</div>
        </StatCard>
        <StatCard>
          <div className="stat-value">₹{(stats.totalWithdrawalAmount || 0).toLocaleString()}</div>
          <div className="stat-label">Total Withdrawal Amount</div>
        </StatCard>
        <StatCard>
          <div className="stat-value">{stats.memberSince}</div>
          <div className="stat-label">Member Since</div>
        </StatCard>
      </StatsGrid>

      <ProfileCard>
        <CardHeader>
          <h2>
            <FaUser />
            Personal Information
          </h2>
          {/* Remove the Edit Profile button */}
        </CardHeader>

        <CardContent>
          {/* Always show read-only personal information */}
          <PersonalInfoGrid>
            <InfoCard>
              <div className="label">First Name</div>
              <div className="value">
                <FaUser />
                {user?.firstName || 'Not provided'}
              </div>
            </InfoCard>

            <InfoCard>
              <div className="label">Last Name</div>
              <div className="value">
                <FaUser />
                {user?.lastName || 'Not provided'}
              </div>
            </InfoCard>

            <InfoCard>
              <div className="label">Email Address</div>
              <div className="value">
                <FaEnvelope />
                {user?.email}
              </div>
            </InfoCard>

            <InfoCard>
              <div className="label">Member Since</div>
              <div className="value">
                <FaCalendarAlt />
                {stats.memberSince}
              </div>
            </InfoCard>
          </PersonalInfoGrid>
        </CardContent>
      </ProfileCard>

      {/* Bank Account Details Section */}
      <ProfileCard>
        <CardHeader>
          <h2>
            <FaUniversity />
            Bank Account Details
          </h2>
          <button
            onClick={editingBankDetails ? handleBankCancel : handleBankEdit}
            disabled={bankLoading}
            aria-label={editingBankDetails ? 'Cancel editing bank details' : 'Edit bank details'}
          >
            {editingBankDetails ? <FaTimes /> : <FaEdit />}
            {editingBankDetails ? 'Cancel' : 'Edit'}
          </button>
        </CardHeader>
        <CardContent>
          {editingBankDetails ? (
            <>
              <FormGroup>
                <label>Account Holder Name</label>
                <div className="input-wrapper">
                  <FaUser />
                  <Input
                    type="text"
                    className={!bankValidation.accountHolderName.isValid ? 'error' : ''}
                    value={bankData.accountHolderName}
                    onChange={(e) => handleBankInputChange('accountHolderName', e.target.value)}
                    placeholder="Enter account holder name"
                    aria-label="Account holder name"
                  />
                </div>
                {!bankValidation.accountHolderName.isValid && (
                  <ValidationMessage type="error" role="alert" aria-live="polite">
                    <FaTimesIcon /> {bankValidation.accountHolderName.message}
                  </ValidationMessage>
                )}
              </FormGroup>

              <FormGroup>
                <label>Account Number</label>
                <div className="input-wrapper">
                  <FaCreditCard />
                  <Input
                    type="text"
                    className={!bankValidation.accountNumber.isValid ? 'error' : ''}
                    value={bankData.accountNumber}
                    onChange={(e) => handleBankInputChange('accountNumber', e.target.value)}
                    placeholder="Enter account number"
                    aria-label="Account number"
                  />
                </div>
                {!bankValidation.accountNumber.isValid && (
                  <ValidationMessage type="error" role="alert" aria-live="polite">
                    <FaTimesIcon /> {bankValidation.accountNumber.message}
                  </ValidationMessage>
                )}
              </FormGroup>

              <FormGroup>
                <label>IFSC Code</label>
                <div className="input-wrapper">
                  <FaUniversity />
                  <Input
                    type="text"
                    className={!bankValidation.ifscCode.isValid ? 'error' : ''}
                    value={bankData.ifscCode}
                    onChange={(e) => handleBankInputChange('ifscCode', e.target.value.toUpperCase())}
                    placeholder="Enter IFSC code (e.g., SBIN0123456)"
                    aria-label="IFSC code"
                  />
                </div>
                {!bankValidation.ifscCode.isValid && (
                  <ValidationMessage type="error" role="alert" aria-live="polite">
                    <FaTimesIcon /> {bankValidation.ifscCode.message}
                  </ValidationMessage>
                )}
              </FormGroup>

              <FormActions>
                <button
                  type="button"
                  className="cancel"
                  onClick={handleBankCancel}
                  disabled={bankLoading}
                  aria-label="Cancel bank details changes"
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="button"
                  className="save"
                  onClick={handleBankSave}
                  disabled={bankLoading || !bankValidation.isFormValid || !isOnline}
                  aria-label="Save bank details"
                >
                  {bankLoading ? (
                    <><LoadingSpinner /> {bankRetryCount > 0 ? `Retrying... (${bankRetryCount}/3)` : 'Saving...'}</>
                  ) : (
                    <><FaSave /> Save Details</>
                  )}
                </button>
              </FormActions>

              {/* Network status indicator */}
              {!isOnline && (
                <ValidationMessage type="error" role="alert" aria-live="polite">
                  <FaTimesIcon /> No internet connection. Please check your network.
                </ValidationMessage>
              )}

              {/* Retry information */}
              {bankRetryCount > 0 && bankLoading && (
                <ValidationMessage type="info" role="alert" aria-live="polite">
                  <LoadingSpinner /> Attempting to save... (Retry {bankRetryCount}/3)
                </ValidationMessage>
              )}
            </>
          ) : (
            <PersonalInfoGrid>
              <InfoCard>
                <div className="label">Account Holder Name</div>
                <div className="value">
                  <FaUser />
                  {bankData.accountHolderName || 'Not provided'}
                </div>
              </InfoCard>

              <InfoCard>
                <div className="label">Account Number</div>
                <div className="value">
                  <FaCreditCard />
                  {bankData.accountNumber || 'Not provided'}
                </div>
              </InfoCard>

              <InfoCard>
                <div className="label">IFSC Code</div>
                <div className="value">
                  <FaUniversity />
                  {bankData.ifscCode || 'Not provided'}
                </div>
              </InfoCard>
            </PersonalInfoGrid>
          )}
        </CardContent>
      </ProfileCard>

      <ProfileCard>
        <CardHeader>
          <h2>
            <FaLock />
            Password & Security
          </h2>
          {/* Password-only users (no Google) */}
          {!user?.googleId && hasPassword ? (
            <>
              {!editingPassword && (
                <button onClick={handlePasswordEdit}>
                  <FaEdit />
                  Change Password
                </button>
              )}
              <button onClick={handleLinkGoogle} style={{ marginLeft: '0.5rem' }}>
                <FaGoogle />
                Link Google Account
              </button>
            </>
          ) : /* Hybrid users (both Google and password) */ user?.googleId && hasPassword ? (
            !editingPassword && (
              <button onClick={handlePasswordEdit}>
                <FaEdit />
                Change Password
              </button>
            )
          ) : null}
        </CardHeader>

        <CardContent>
          {/* User Type Indicator */}
          {(() => {
            const userTypeInfo = getUserTypeInfo();
            const IconComponent = userTypeInfo?.icon;

            return (
              <UserTypeIndicator type={userTypeInfo?.type}>
                <div className={`user-type-icon ${userTypeInfo?.type}`}>
                  {IconComponent && <IconComponent />}
                </div>
                <div className="user-type-info">
                  <div className="user-type-title">{userTypeInfo?.title}</div>
                  <div className="user-type-description">{userTypeInfo?.description}</div>
                </div>
              </UserTypeIndicator>
            );
          })()}

          {/* Google-only users - show info message */}
          {user?.googleId && !hasPassword ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FaGoogle style={{
                fontSize: '3rem',
                color: '#4285f4',
                marginBottom: '1rem',
                display: 'block',
                margin: '0 auto 1rem auto'
              }} />
              <p style={{
                color: '#7f8c8d',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                You signed up with Google. Your account is secured through Google authentication.
              </p>
            </div>
          ) : editingPassword ? (
            <>
              {hasPassword && (
                <FormGroup>
                  <label>Current Password</label>
                  <div className="input-wrapper">
                    <FaLock />
                    <Input
                      type={showPasswords.current ? 'text' : 'password'}
                      className="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => handleInputChange('currentPassword', e.target.value, 'password')}
                      placeholder="Enter your current password"
                      aria-label="Current password"
                      aria-describedby="current-password-help"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => togglePasswordVisibility('current')}
                    >
                      {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </FormGroup>
              )}

              <FormGroup>
                <label>New Password</label>
                <div className="input-wrapper">
                  <FaLock />
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    className="password"
                    value={passwordData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value, 'password')}
                    placeholder="Enter your new password"
                    aria-label="New password"
                    aria-describedby="new-password-help password-requirements"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('new')}
                    aria-label={showPasswords.new ? 'Hide new password' : 'Show new password'}
                    tabIndex={0}
                  >
                    {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {passwordData.newPassword && (
                  <PasswordStrengthIndicator strength={passwordValidation.strength}>
                    <div className="strength-bar">
                      <div className={`strength-fill ${passwordValidation.strength}`}></div>
                    </div>
                    <span className="strength-text">
                      Password strength: {passwordValidation.strength.charAt(0).toUpperCase() + passwordValidation.strength.slice(1)}
                    </span>
                  </PasswordStrengthIndicator>
                )}

                {/* Password Requirements */}
                {passwordData.newPassword && (
                  <div id="password-requirements" role="region" aria-label="Password requirements">
                    {renderPasswordRequirements()}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <label>Confirm New Password</label>
                <div className="input-wrapper">
                  <FaLock />
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    className={`password ${passwordData.confirmPassword && !passwordValidation.confirmMatch ? 'error' : ''}`}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value, 'password')}
                    placeholder="Confirm your new password"
                    aria-label="Confirm new password"
                    aria-describedby="confirm-password-help confirm-password-validation"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => togglePasswordVisibility('confirm')}
                    aria-label={showPasswords.confirm ? 'Hide confirm password' : 'Show confirm password'}
                    tabIndex={0}
                  >
                    {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                {/* Password Confirmation Validation */}
                {passwordData.confirmPassword && (
                  <ValidationMessage
                    id="confirm-password-validation"
                    type={passwordValidation.confirmMatch ? 'success' : 'error'}
                    role="alert"
                    aria-live="polite"
                  >
                    {passwordValidation.confirmMatch ? (
                      <><FaCheck /> Passwords match</>
                    ) : (
                      <><FaTimesIcon /> Passwords do not match</>
                    )}
                  </ValidationMessage>
                )}
              </FormGroup>

              <FormActions>
                <button
                  type="button"
                  className="cancel"
                  onClick={handlePasswordCancel}
                  aria-label="Cancel password change"
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="button"
                  className="save"
                  onClick={handlePasswordSave}
                  disabled={loading || !passwordValidation.isValid || (!hasPassword ? false : !passwordData.currentPassword)}
                  aria-label="Save new password"
                  aria-describedby={!passwordValidation.isValid ? 'password-requirements' : undefined}
                >
                  {loading ? (
                    <><LoadingSpinner /> Updating...</>
                  ) : (
                    <><FaSave /> Update Password</>
                  )}
                </button>
              </FormActions>
            </>
          ) : (
            <div>
              {user?.googleId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <FaGoogle style={{ color: '#4285f4' }} />
                  <span style={{ color: '#7f8c8d' }}>Google account linked</span>
                </div>
              ) : null}
              <p style={{ color: '#7f8c8d', margin: 0 }}>
                {/* Google-only users */}
                {user?.googleId && !hasPassword
                  ? 'You login with Google authentication. Your account security is managed through Google.'
                  /* Password-only users */
                  : !user?.googleId && hasPassword
                    ? 'Your password is secure. Click "Change Password" to update it or link your Google account for additional login options.'
                    /* Hybrid users */
                    : user?.googleId && hasPassword
                      ? 'You can login with both Google and email/password. Click "Change Password" to update your password.'
                      : 'Please contact support for account assistance.'
                }
              </p>
            </div>
          )}
        </CardContent>
      </ProfileCard>


    </ProfileContainer>
  );
};

export default Profile;
